'use client';
// GED-11 / GED-12 — Planification d'une revue de direction (ISO 15189 §8.9).
//
// ── Pourquoi ce composant vit ICI et non dans l'écran « Revue de direction » ──
//
// GED-12 demande de pouvoir planifier une revue DEPUIS LE CALENDRIER. La façon
// paresseuse de l'obtenir serait d'écrire un second formulaire dans le tableau
// de bord. Ce serait un second CHEMIN DE CRÉATION, et un second chemin diverge
// toujours du premier : celui du calendrier n'aurait pas connu les entrées, ou
// pas les durées, ou pas les participants — et personne ne l'aurait vu, puisque
// les deux créent des objets valides.
//
// Il n'y a donc qu'UNE création, dans ce fichier, appelée de deux endroits :
// l'écran « Revue de direction » et le calendrier du tableau de bord. Elle
// appelle une seule route (`POST ged/rdd`) et produit un seul et même objet.
// La seule différence entre les deux appels est `dateInitiale`, préremplie par
// le jour cliqué au calendrier.
//
// ── L'ordre du jour se choisit ICI, pas après ────────────────────────────────
//
// Les entrées de la revue étaient une constante du code. Elles viennent
// désormais du paramétrage du laboratoire, et cet écran permet d'en écarter ou
// d'en rétablir pour CETTE séance, et d'ajuster les durées. Ce qui est retenu
// est FIGÉ par le serveur au moment de la création : modifier le paramétrage
// plus tard ne réécrira pas cette revue.
//
// Ce que l'écran refuse de laisser faire : décocher une entrée exigée par la
// norme. La case est désactivée, la clause est affichée, et le motif est écrit
// à côté — plutôt que de laisser le serveur refuser après le clic, et plutôt
// que de compter sur la mémoire de la RQ.
import React, { useEffect, useMemo, useState } from 'react';
import { useGed } from '@/lib/store';
import { VOILE,
  I, card, inputStyle,
  FieldError, RequiredHint, useBusy, useEscape } from '@/components/ui';
import type { GedRddParametrage, GedRddEntreeCatalogue } from '@/lib/types';

import { PiedActions } from '@/shared/PiedActions';
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5,
};

/** « 3 h 20 » plutôt que « 200 min » : c'est en heures qu'on juge si une revue
 *  tient dans une matinée. Même règle que `formaterDuree` côté serveur — la
 *  duplication est assumée ici, c'est du formatage d'affichage, pas un calcul. */
export function formaterDuree(minutes: number): string {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${String(r).padStart(2, '0')}` : `${h} h`;
}

export function PlanifierRevue({ onFerme, dateInitiale = '' }: {
  onFerme: () => void;
  /** Jour cliqué au calendrier. Vide quand on planifie depuis le registre. */
  dateInitiale?: string;
}) {
  const { smq, settings, session } = useGed();
  const gedUsers = settings?.gedUsers || [];
  const [dateRevue, setDateRevue] = useState(dateInitiale);
  const [choisis, setChoisis] = useState<Record<string, boolean>>(() => ({ [session.key]: true }));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  useEscape(!estOccupe(), onFerme);

  // Ordre du jour : catalogue normatif + réglage du laboratoire, servis par le
  // serveur. Le catalogue n'est PAS redéclaré ici : un second référentiel de
  // clés aurait fini par désigner des rubriques qui n'existent plus, et une
  // entrée mal raccordée ne produit aucune erreur — elle produit une revue sans
  // chiffres.
  const [param, setParam] = useState<GedRddParametrage | null>(null);
  const [retenues, setRetenues] = useState<Record<string, boolean>>({});
  const [durees, setDurees] = useState<Record<string, number>>({});

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rdd-parametrage', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: GedRddParametrage | null) => {
        if (!vivant || !j) { if (vivant) setParam(null); return; }
        setParam(j);
        const r: Record<string, boolean> = {};
        const d: Record<string, number> = {};
        j.items.forEach((it) => { r[it.cle] = it.actif; d[it.cle] = it.dureeMin; });
        setRetenues(r); setDurees(d);
      })
      .catch(() => { if (vivant) setParam(null); });
    return () => { vivant = false; };
  }, []);

  const parCle = useMemo(() => {
    const m = new Map<string, GedRddEntreeCatalogue>();
    (param?.catalogue || []).forEach((c) => m.set(c.cle, c));
    return m;
  }, [param]);

  const items = param?.items || [];
  const actives = items.filter((i) => retenues[i.cle]);
  const total = actives.reduce((n, i) => n + (durees[i.cle] || 0), 0);
  const participants = Object.keys(choisis).filter((k) => choisis[k]);

  return (
    <div style={{ ...card, padding: 18, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>Planifier une revue de direction</h2>
        <RequiredHint />
      </div>
      <div style={{ maxWidth: 260 }}>
        <label style={lbl} htmlFor="rdd-date">Date de la revue <span style={{ color: 'var(--red-600)' }}>*</span></label>
        <input id="rdd-date" type="date" autoFocus value={dateRevue}
          onChange={(e) => { setDateRevue(e.target.value); if (erreur) setErreur(''); }}
          aria-invalid={!!erreur} style={{ ...inputStyle, borderColor: erreur ? 'var(--red-600)' : undefined }} />
        <FieldError>{erreur}</FieldError>
      </div>

      <div>
        <label style={lbl}>Participants ({participants.length})</label>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {gedUsers.map((u) => (
            <label key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, background: choisis[u.id] ? 'var(--cyan-50)' : 'var(--surface-sunken)', border: `1px solid ${choisis[u.id] ? 'var(--cyan-200)' : 'var(--border-default)'}`, borderRadius: 'var(--radius-pill)', padding: '5px 12px', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!choisis[u.id]} onChange={(e) => setChoisis({ ...choisis, [u.id]: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
              {u.nom}
            </label>
          ))}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6 }}>Les participants sont imprimés au compte-rendu signé.</div>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={lbl}>Ordre du jour ({actives.length} entrée{actives.length > 1 ? 's' : ''})</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: total > 240 ? 'var(--amber-600)' : 'var(--text-strong)' }}>
            Durée cumulée : {formaterDuree(total)}
          </span>
        </div>
        <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '84ch' }}>
          Ces entrées et leurs durées sont <strong>figées</strong> à la planification : modifier le
          paramétrage plus tard ne changera pas l’ordre du jour de cette revue.
          {total > 240 && <> Au-delà de quatre heures cumulées, la séance se prépare autrement — envisagez de la scinder.</>}
        </p>
        {!param && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture du paramétrage des entrées…</div>
        )}
        {param && (
          <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
            {items.map((it, i) => {
              const cat = parCle.get(it.cle);
              const normatif = !!cat?.normatif;
              const alimente = !!cat?.rubrique;
              return (
                <div key={it.cle} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', background: retenues[it.cle] ? 'transparent' : 'var(--surface-sunken)' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: '1 1 320px', cursor: normatif ? 'not-allowed' : 'pointer' }}
                    title={normatif ? `Entrée exigée par la norme (${cat?.clause}) : elle ne peut pas être retirée.` : cat?.aide}>
                    <input type="checkbox" checked={!!retenues[it.cle]} disabled={normatif}
                      onChange={(e) => setRetenues({ ...retenues, [it.cle]: e.target.checked })}
                      style={{ accentColor: 'var(--color-primary)' }} />
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: retenues[it.cle] ? 'var(--text-strong)' : 'var(--text-muted)' }}>{it.libelle}</span>
                    {normatif && (
                      <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 8px', background: 'var(--navy-800)', color: '#fff' }}>
                        exigé {cat?.clause}
                      </span>
                    )}
                    <span title={alimente
                      ? 'Cette entrée arrive en séance AVEC ses chiffres, extraits des enregistrements de la période (dossier de revue).'
                      : 'Aucun chiffre n’est extrait pour cette entrée : elle se documente à la main en séance.'}
                      style={{ fontSize: 10, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 8px', cursor: 'help', background: alimente ? 'var(--green-50, #ecfdf5)' : 'var(--gray-100, #f1f1f1)', color: alimente ? 'var(--green-600)' : 'var(--text-muted)' }}>
                      {alimente ? 'chiffres extraits' : 'à remplir à la main'}
                    </span>
                  </label>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" min={0} max={600} step={5} value={durees[it.cle] ?? 0}
                      disabled={!retenues[it.cle]}
                      aria-label={`Durée de « ${it.libelle} » en minutes`}
                      onChange={(e) => setDurees({ ...durees, [it.cle]: Math.max(0, Number(e.target.value) || 0) })}
                      style={{ ...inputStyle, height: 30, width: 76, fontSize: 12.5, textAlign: 'right' }} />
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>min</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PiedActions taille="sm" annuler={{ onClick: onFerme }} principale={{ libelle: 'Planifier', libelleChargement: 'Planification…', chargement: estOccupe('plan'), disabled: estOccupe() || !param, onClick: () => void run('plan', async () => {
          if (!dateRevue) { setErreur('La date de la revue est obligatoire.'); return; }
          setErreur('');
          const entrees = actives.map((i) => ({ cle: i.cle, libelle: i.libelle, dureeMin: durees[i.cle] || 0 }));
          const ok = await smq('ged/rdd', { dateRevue, participants, entrees },
            `Revue planifiée — ${entrees.length} entrée(s), ${formaterDuree(total)}.`);
          if (ok) onFerme();
        }) }} />
    </div>
  );
}

/** Le même formulaire, posé en surcouche — c'est la forme qu'il prend quand on
 *  l'ouvre depuis le calendrier, où il n'y a pas de place dans le flux. Le
 *  formulaire, lui, est le même objet : GED-12 ne double pas la création. */
export function PlanifierRevueOverlay({ onFerme, dateInitiale }: { onFerme: () => void; dateInitiale?: string }) {
  return (
    <div role="dialog" aria-modal="true" aria-label="Planifier une revue de direction"
      style={{ position: 'fixed', inset: 0, zIndex: 400, background: VOILE, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' }}>
      <div style={{ width: 'min(760px, 100%)' }} onMouseDown={(e) => e.stopPropagation()}>
        <PlanifierRevue onFerme={onFerme} dateInitiale={dateInitiale} />
      </div>
    </div>
  );
}
